/**
 * NotifyRequestsTab.tsx — global ADMIN list of upcoming-service expressions of
 * interest. It is deliberately independent of clinic configuration: these
 * requests come from pages whose services are not live clinics yet.
 */
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import {
  getNotificationRequests,
  type NotificationRequestRow,
} from '../api/clinic';
import { BOOKING_ORIGIN } from '../lib/bookingSite';
import { formatUtcDateTime } from '../lib/format';
import { SectionTitle } from '../staff/ui';
import { DataTable, Td, Th } from '../ui/Table';
import { Button, ErrorState, Help, Select } from '../ui/kit';
import { TableSkeleton } from '../ui/skeletons';
import { NOTIFY_SERVICES } from '../notify-widget/services';

function isApprovedBookingUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.origin === BOOKING_ORIGIN && url.protocol === 'https:';
  } catch {
    return false;
  }
}

export function NotifyRequestsTab() {
  const [items, setItems] = useState<NotificationRequestRow[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [serviceKey, setServiceKey] = useState('');
  const requestSequence = useRef(0);

  const load = useCallback(
    (before?: string) => {
      const requestId = ++requestSequence.current;
      setStatus((current) => (before && current === 'ready' ? current : 'loading'));
      setLoadingMore(Boolean(before));
      getNotificationRequests({
        search: search || undefined,
        serviceKey: serviceKey || undefined,
        limit: 50,
        before,
      })
        .then((response) => {
          if (requestId !== requestSequence.current) return;
          setItems((current) => (before ? [...current, ...response.items] : response.items));
          setCursor(response.nextCursor);
          setStatus('ready');
        })
        .catch(() => {
          if (requestId === requestSequence.current) setStatus('error');
        })
        .finally(() => {
          if (requestId === requestSequence.current) setLoadingMore(false);
        });
    },
    [search, serviceKey],
  );

  useEffect(() => {
    load();
  }, [load]);

  function submitSearch(event: FormEvent) {
    event.preventDefault();
    setSearch(searchInput.trim());
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--gps-s-3)' }}>
      <div>
        <SectionTitle>Notify requests</SectionTitle>
        <Help style={{ marginBottom: 0 }}>
          People who asked to be notified when an upcoming service becomes available. No emails
          are sent from this screen.
        </Help>
      </div>

      <form
        onSubmit={submitSearch}
        style={{ display: 'flex', gap: 'var(--gps-s-2)', alignItems: 'end', flexWrap: 'wrap' }}
      >
        <label style={{ display: 'grid', gap: '6px', minWidth: '240px', flex: '1 1 260px' }}>
          <span style={{ fontWeight: 700 }}>Search name or email</span>
          <input
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            maxLength={100}
            style={{
              minHeight: '44px',
              border: '1px solid var(--gps-border-strong)',
              borderRadius: 'var(--gps-r-md)',
              padding: '8px 12px',
              font: 'inherit',
            }}
          />
        </label>
        <label style={{ display: 'grid', gap: '6px', minWidth: '240px', flex: '1 1 260px' }}>
          <span style={{ fontWeight: 700 }}>Requested service</span>
          <Select value={serviceKey} onChange={(event) => setServiceKey(event.target.value)}>
            <option value="">All services</option>
            {NOTIFY_SERVICES.map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </Select>
        </label>
        <Button type="submit" style={{ width: 'auto', minHeight: '44px' }}>
          Search
        </Button>
      </form>

      {status === 'loading' ? (
        <TableSkeleton rows={5} columns={5} label="Loading notification requests…" />
      ) : status === 'error' ? (
        <ErrorState message="Sorry — we could not load notification requests." onRetry={() => load()} />
      ) : items.length === 0 ? (
        <Help style={{ marginBottom: 0 }}>No notification requests have been submitted yet.</Help>
      ) : (
        <>
          <DataTable
            label="Notification requests"
            head={
              <>
                <Th>Name</Th>
                <Th>Email</Th>
                <Th>Requested service</Th>
                <Th>Page</Th>
                <Th>Submitted</Th>
              </>
            }
          >
            {items.map((item) => (
              <tr key={item.id}>
                <Td>{item.name}</Td>
                <Td>{item.email}</Td>
                <Td>{item.serviceName}</Td>
                <Td>
                  {isApprovedBookingUrl(item.pageUrl) ? (
                    <a href={item.pageUrl} target="_blank" rel="noopener noreferrer">
                      View page
                    </a>
                  ) : (
                    item.pageUrl
                  )}
                </Td>
                <Td style={{ whiteSpace: 'nowrap' }}>{formatUtcDateTime(item.createdAt)}</Td>
              </tr>
            ))}
          </DataTable>
          {cursor ? (
            <Button
              type="button"
              variant="ghost"
              style={{ width: 'auto', justifySelf: 'center' }}
              onClick={() => load(cursor)}
              disabled={loadingMore}
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </Button>
          ) : null}
        </>
      )}
    </div>
  );
}
